import { PrepList, EmailPreview, OrderDayPreview, OrderQuote, VendorShort, CrmTrends, ForecastAhead, ItemUsda, UsdaCommodity, AssistantAnswer, AssistantPasteAnswer, AssistantStatus, ItemProfile, QboBalances, QboLinkCreated, QboMatching, QboMatchRun, QboSwitchMatching, QboSync, InvoiceAdjustment, LostSale, LostSalesReport, PayLink, SquareStatus, Workload, WorkloadSettings, type ItemCreate, EmailLogEntry, EmailStatus, type EmailSettings, type DocumentEmailSend, PublicInvoice, PublicStatement, QboCheck, QboConnectUrl, QboPullStarted, QboStatus, SalesAnalysis, OrderHistoryRow, VendorOffer, VendorPrices, VendorSheets, CustomerMetrics, OpeningBalances, type OpeningBalancesPut, PriceSheetRow, JobList, JobRun, Signals, PacaLetter, PacaNotice, PacaSettings, CompanySettings, PaymentReceipt, VendorClaim, BillMatching, CollectionRow, ComplaintRow, NoCostRow, PaymentPromise, BooksSettings, InventoryValue, UndepositedPayment, Deposit, DepositSummary, DaySummary, CustomerPattern, ExpectedOrder, OrderDay, OrderDaySetting, LotTrace, TraceRow, Passkey, PasskeyOptions, SystemHealth, Payables, VendorPayment, PriceList, PricePublished, PriceWorksheet, ManagedUser, Company, Site, Transfer, Activity, ApiToken, ApiTokenIssued, Contact, ExceptionsReport, Followups, OAuthApproved, OAuthClientInfo, PortalCatalogItem, PortalHome, QboMirrorStatus, QboMirrorTurnedOn, QboWriteDrained, QboWriteStatus, PracticeCounts, QboInvoiceDifference, WeatherDay, Renewal, Temperature, FoodSafetySettings, Vehicle, VehicleLog, type RenewalPut, type TemperatureCreate, type VehicleCreate, type VehiclePatch, type VehicleLogCreate, MarginSettings, StalePrice, Suggestion, PortalInvoiceRow, PortalSettings, AgingReport, TaxSettings, AudienceMember, BlastPreview, BlastResult, Message, MessagingStatus, Route, RouteDriver, Vendor, Competitor, CompetitorPrice, CountResult, CountSheet, Credit, Customer, CustomerSummary, Dashboard, FileInfo, Invoice, InvoiceSummary, Item, ItemAlias, ItemCatalog, ItemIntel, ItemPriceHistory, LedgerSettings, Lot, Me, Order, Payment, PickList, PriceTier, Problem, ProfitReport, PurchaseOrder, Receipt, ReceiptSummary, ShrinkResult, StandingOrder, StandingRun, Statement, UnknownName, UnknownNameAssigned, UnknownNameSuggestions, VendorDoc, VendorDocDraft, VendorDocSummary, VendorSummary, PaperAdded, PaperList, GmailStatus, GmailRead, MailPapersKnownResult, PaperKeepResult, VendorCodeRow, VendorCodeChanged, LinkingPreview, LinkingApplied, LinkingIgnored, CloverCategories, CloverFilterSaved, PriceChanges, CostDash, CostDashItem, type LinkingApply, ActivityPage, ItemCategory, BooksSwitchStatus, QboPnl } from '@morales/shared';
import type { PrepPrinted, OrderRevise, OrderQuoteAsk, VendorShortTyped, UsdaLink, AssistantPaste, AssistantAsk, ItemProfilePut, QboLinkEntity, InvoiceShorts, InvoiceEdit, EmailPreviewAsk, LostSaleCreate, QboEnvironment, SalesGrain, VendorOfferPut, VendorSheetPut, InvoiceCreate, ClientError, CustomerLocationPut, OrderDayAccountPut, RouteCashTurnIn, VendorPaymentCreate, PricePublish, PasswordChange, PasswordLogin, UserCreate, UserPatch, CompanyCreate, SiteCreate, SitePatch, TransferCreate, TransferReceive, ActivityCreate, ApiTokenCreate, BlastRequest, MessageSend, OAuthApprove, PortalOrderCreate, RoutePatch, StopDeliver, StopNotDelivered, StopAnotherDay, CompetitorPriceCreate, ContactCreate, ContactPatch, CustomerPatch, CustomerCreate, VendorCreate, VendorPatch, CustomerStage, CountCreate, CreditCreate, VendorClaimSettle, PromiseCreate, PaymentReceive, ApplyOnAccount, DepositCreate, DayClose, InvoiceFromOrder, ItemAliasCreate, ItemPatch, OrderCreate, PaymentCreate, PurchaseOrderCreate, ReceiptCreate, ShrinkCreate, StandingOrderPut, UnknownNameAssign, UnknownNameSuggest, VendorDocCreate } from '@morales/shared';
import { z } from 'zod';

const API_BASE = (import.meta.env.VITE_API_BASE ?? '/api').replace(/\/+$/, '');
const TIMEOUT_MS = 20_000;

/** Cache the service worker keeps catalog reads in (see vite.config.ts); cleared on sign-out. */
export const API_CACHE = 'morales-api';
/** The driver's day kept for no signal (decision 182): who is signed in, the routes, the sites; cleared with the other. */
export const DAY_CACHE = 'morales-day';
/** Who is signed in and the sites, kept with the driver's day (decision 182). */
export const ME_CACHE = 'morales-me';

/** Every failed call becomes one of these. status 0 = never reached the server (offline, timeout). */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem;
  /** the app itself could not read the answer (a version mismatch after a deploy): its detail is for developers */
  readonly unreadable: boolean;

  constructor(problem: Problem, unreadable = false) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.status = problem.status;
    this.problem = problem;
    this.unreadable = unreadable;
  }
}

export const isNetworkError = (e: unknown): boolean => e instanceof ApiError && e.status === 0;

function withTimeout(outer: AbortSignal | undefined, ms = TIMEOUT_MS): AbortSignal | undefined {
  if (typeof AbortSignal === 'undefined' || typeof AbortSignal.timeout !== 'function') return outer;
  const timeout = AbortSignal.timeout(ms);
  if (!outer) return timeout;
  return typeof AbortSignal.any === 'function' ? AbortSignal.any([outer, timeout]) : outer;
}

interface SendOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal | undefined;
  /** longer than the usual 20 s: a call that waits on Claude */
  timeoutMs?: number;
}

async function send(path: string, opts: SendOptions = {}): Promise<unknown> {
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };
  const init: RequestInit = { method: opts.method ?? 'GET', credentials: 'include', headers };
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }
  const signal = withTimeout(opts.signal, opts.timeoutMs);
  if (signal) init.signal = signal;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, init);
  } catch (err) {
    if (opts.signal?.aborted) throw err; // cancelled by the caller (react-query): not a failure
    throw new ApiError({ status: 0, title: 'Network error', detail: err instanceof Error ? err.message : String(err) });
  }

  const text = await res.text().catch(() => '');
  let data: unknown;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = undefined;
    }
  }
  if (!res.ok) {
    const problem = Problem.safeParse(data);
    throw new ApiError(
      problem.success
        ? { ...problem.data, status: res.status }
        : { status: res.status, title: res.statusText || `HTTP ${res.status}` },
    );
  }
  return data;
}

function parse<S extends z.ZodTypeAny>(schema: S, data: unknown, what: string): z.output<S> {
  const result = schema.safeParse(data);
  if (result.success) return result.data as z.output<S>;
  throw new ApiError(
    {
      status: 502,
      title: 'Unexpected response from the server',
      detail: what,
      errors: result.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })),
    },
    true,
  );
}

async function get<S extends z.ZodTypeAny>(schema: S, path: string, signal?: AbortSignal): Promise<z.output<S>> {
  return parse(schema, await send(path, { signal }), `GET ${path}`);
}

function qs(params: Record<string, string | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) sp.set(k, v);
  const s = sp.toString();
  return s ? `?${s}` : '';
}

const seg = (id: string) => encodeURIComponent(id);

const CodeRequestResult = z.object({ sent: z.boolean(), devCode: z.string().optional() });
export type CodeRequestResult = z.infer<typeof CodeRequestResult>;

const ItemList = z.array(Item);
const CustomerList = z.array(CustomerSummary);
const OrderList = z.array(Order);

/** An upload's answer: JSON from the API, or a proxy's page when it gave up first (then the status says why). */
const jsonOrNull = (text: string): unknown => {
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return null;
  }
};
const uploadFailed = (res: Response, reading = false) =>
  res.status === 504 || res.status === 502
    ? { status: res.status, title: 'No answer in time', detail: reading ? 'The server took too long to answer. For a long price list, split the PDF in two and read each half.' : 'The server took too long to answer: try again.' }
    : res.status === 413
      ? { status: 413, title: 'File too large', detail: 'The file is too large: send a smaller photo or PDF (up to 15 MB).' }
      : { status: res.status, title: res.statusText || 'Error' };

export const api = {
  async requestCode(phone: string): Promise<CodeRequestResult> {
    const data = await send('/auth/code/request', { method: 'POST', body: { phone } });
    return parse(CodeRequestResult, data, 'POST /auth/code/request');
  },
  async verifyCode(phone: string, code: string): Promise<Me> {
    const data = await send('/auth/code/verify', { method: 'POST', body: { phone, code } });
    return parse(Me, data, 'POST /auth/code/verify');
  },
  async passwordLogin(body: PasswordLogin): Promise<Me> {
    return parse(Me, await send('/auth/password/login', { method: 'POST', body }), 'POST /auth/password/login');
  },
  async changePassword(body: PasswordChange): Promise<void> {
    await send('/auth/password', { method: 'POST', body });
  },
  customerPattern: (id: string, signal?: AbortSignal) => get(CustomerPattern, `/customers/${seg(id)}/pattern`, signal),
  expectedOrders: (from: string, to: string, signal?: AbortSignal) => get(z.array(ExpectedOrder), `/forecast/expected${qs({ from, to })}`, signal),
  orderDay: (q: { date?: string; load?: number; extra?: string | null }, signal?: AbortSignal) => get(OrderDay, `/order-day${qs({ ...(q.date ? { date: q.date } : {}), ...(q.load ? { load: String(q.load) } : {}) })}${q.extra !== undefined && q.extra !== null ? `${q.date || q.load ? '&' : '?'}extra=${encodeURIComponent(q.extra)}` : ''}`, signal),
  orderDays: (signal?: AbortSignal) => get(z.array(OrderDaySetting), '/order-days', signal),
  async orderDayAccount(customerId: string, body: OrderDayAccountPut): Promise<OrderDay> {
    return parse(OrderDay, await send(`/order-day/accounts/${seg(customerId)}`, { method: 'PUT', body }), 'PUT /order-day/accounts');
  },
  /** what "Invoice the accounts" would make, nothing written (decision 175) */
  async orderDayInvoicePreview(body: { date?: string; load?: number; extra?: string[] }): Promise<OrderDayPreview[]> {
    return parse(z.array(OrderDayPreview), await send('/order-day/invoices/preview', { method: 'POST', body }), 'POST /order-day/invoices/preview');
  },
  orderDayVendors: (q: { date: string; load: number }, signal?: AbortSignal) => get(VendorSheets, `/order-day/vendors${qs({ date: q.date, load: String(q.load) })}`, signal),
  // decision 154: one item on a vendor's order for the load from the plan; the call-ahead columns kept with a load
  async orderDayVendorLine(vendorId: string, body: { date: string; load: number; itemId: string; qty: number; takeOffVendorId?: string; unitCost?: number }): Promise<VendorSheets> {
    return parse(VendorSheets, await send(`/order-day/vendors/${seg(vendorId)}/line`, { method: 'POST', body }), 'POST vendor line');
  },
  async saveCallAheads(load: number, customerIds: string[]): Promise<{ load: number; customerIds: string[] }> {
    return parse(z.object({ load: z.number(), customerIds: z.array(z.string()) }), await send('/order-day/call-aheads', { method: 'PUT', body: { load, customerIds } }), 'PUT call-aheads');
  },
  async saveVendorSheet(vendorId: string, body: VendorSheetPut): Promise<VendorSheets> {
    return parse(VendorSheets, await send(`/order-day/vendors/${seg(vendorId)}`, { method: 'PUT', body }), 'PUT /order-day/vendors');
  },
  async vendorSheetAction(vendorId: string, action: 'place' | 'reopen' | 'close', body: { date: string; load: number }): Promise<VendorSheets> {
    return parse(VendorSheets, await send(`/order-day/vendors/${seg(vendorId)}/${action}`, { method: 'POST', body }), `POST /order-day/vendors/${action}`);
  },
  // decision 176: what the vendors are short of (open ones, or a load's), what the vendor said typed in, handled
  vendorShorts: (q: { date?: string; load?: number }, signal?: AbortSignal) =>
    get(z.array(VendorShort), `/vendor-shorts${qs(q.date ? { date: q.date, ...(q.load ? { load: String(q.load) } : {}) } : {})}`, signal),
  async typeVendorShort(body: VendorShortTyped): Promise<VendorShort[]> {
    return parse(z.array(VendorShort), await send('/vendor-shorts/typed', { method: 'PUT', body }), 'PUT /vendor-shorts/typed');
  },
  async vendorShortDone(id: string, done: boolean): Promise<VendorShort> {
    return parse(VendorShort, await send(`/vendor-shorts/${seg(id)}/done`, { method: 'POST', body: { done } }), 'POST /vendor-shorts/done');
  },
  vendorPrices: (q: { from?: string; to?: string; vendor?: string; item?: string }, signal?: AbortSignal) => get(VendorPrices, `/vendor-prices${qs(q)}`, signal),
  vendorOffers: (signal?: AbortSignal) => get(z.array(VendorOffer), '/vendor-offers', signal),
  vendorOffer: (vendorId: string, signal?: AbortSignal) => get(VendorOffer, `/vendor-offers/${seg(vendorId)}`, signal),
  async saveVendorOffer(vendorId: string, body: VendorOfferPut): Promise<VendorOffer> {
    return parse(VendorOffer, await send(`/vendor-offers/${seg(vendorId)}`, { method: 'PUT', body }), 'PUT /vendor-offers');
  },
  orderHistory: (q: { from?: string; to?: string; vendor?: string; shorts?: boolean }, signal?: AbortSignal) =>
    get(z.array(OrderHistoryRow), `/order-day/history${qs({ ...(q.from ? { from: q.from } : {}), ...(q.to ? { to: q.to } : {}), ...(q.vendor ? { vendor: q.vendor } : {}), ...(q.shorts ? { shorts: 'true' } : {}) })}`, signal),
  lotTrace: (code: string, signal?: AbortSignal) => get(LotTrace, `/trace/lot/${seg(code)}`, signal),
  traceExport: (q: { from: string; to: string; item?: string; customer?: string; lot?: string }, signal?: AbortSignal) => get(z.array(TraceRow), `/trace/export${qs(q)}`, signal),
  passkeys: (signal?: AbortSignal) => get(z.array(Passkey), '/auth/passkeys', signal),
  async passkeyRegisterOptions(): Promise<PasskeyOptions> {
    return parse(PasskeyOptions, await send('/auth/passkeys/register/options', { method: 'POST' }), 'POST /auth/passkeys/register/options');
  },
  async passkeyRegisterVerify(challengeId: string, response: Record<string, unknown>, deviceLabel?: string): Promise<Passkey> {
    return parse(Passkey, await send('/auth/passkeys/register/verify', { method: 'POST', body: { challengeId, response, ...(deviceLabel ? { deviceLabel } : {}) } }), 'POST /auth/passkeys/register/verify');
  },
  async forgetPasskey(id: string): Promise<void> {
    await send(`/auth/passkeys/${seg(id)}`, { method: 'DELETE' });
  },
  async passkeyLoginOptions(): Promise<PasskeyOptions> {
    return parse(PasskeyOptions, await send('/auth/passkeys/login/options', { method: 'POST' }), 'POST /auth/passkeys/login/options');
  },
  async passkeyLoginVerify(challengeId: string, response: Record<string, unknown>): Promise<Me> {
    return parse(Me, await send('/auth/passkeys/login/verify', { method: 'POST', body: { challengeId, response } }), 'POST /auth/passkeys/login/verify');
  },
  async reportClientError(body: ClientError): Promise<void> {
    await send('/client-errors', { method: 'POST', body });
  },
  systemHealth: (signal?: AbortSignal) => get(SystemHealth, '/system/health', signal),
  systemJobs: (signal?: AbortSignal) => get(JobList, '/system/jobs', signal),
  qboStatus: (signal?: AbortSignal) => get(QboStatus, '/qbo/status', signal),
  qboCheck: (signal?: AbortSignal) => get(QboCheck, '/qbo/check', signal),
  qboMatching: (signal?: AbortSignal) => get(QboMatching, '/qbo/matching', signal),
  /** what the switch still needs linked (decision 189) */
  qboSwitchMatching: (signal?: AbortSignal) => get(QboSwitchMatching, '/qbo/matching/switch', signal),
  /** copy the customers or the products from QuickBooks (decision 190); apply false is the preview */
  async qboSync(entity: 'Customer' | 'Item', apply: boolean): Promise<QboSync> {
    return parse(QboSync, await send('/qbo/sync', { method: 'POST', body: { entity, apply } }), 'POST /qbo/sync');
  },
  async qboNotAProduct(qboId: string): Promise<void> {
    await send('/qbo/matching/not-a-product', { method: 'POST', body: { qboId } });
  },
  qboBalances: (signal?: AbortSignal, source: 'import' | 'report' = 'import') => get(QboBalances, source === 'report' ? '/qbo/balances?source=report' : '/qbo/balances', signal),
  qboMirror: (signal?: AbortSignal) => get(QboMirrorStatus, '/qbo/mirror', signal),
  qboMirrorCheck: (signal?: AbortSignal) => get(z.array(QboInvoiceDifference), '/qbo/mirror/check', signal),
  async qboMirrorTurnOn(): Promise<QboMirrorTurnedOn> {
    return parse(QboMirrorTurnedOn, await send('/qbo/mirror/turn-on', { method: 'POST', body: { confirm: 'TURN ON', backupDone: true } }), 'POST /qbo/mirror/turn-on');
  },
  booksSwitch: (signal?: AbortSignal) => get(BooksSwitchStatus, '/qbo/switch', signal),
  pnl: (q: { from: string; to: string; by: 'total' | 'month' }, signal?: AbortSignal) => get(QboPnl, `/reports/pnl?from=${q.from}&to=${q.to}&by=${q.by}`, signal),
  async booksSwitchStart(): Promise<void> {
    await send('/qbo/switch', { method: 'POST', body: { confirm: 'SWITCH', backupDone: true } });
  },
  qboWrite: (signal?: AbortSignal) => get(QboWriteStatus, '/qbo/write', signal),
  async qboWriteTurnOn(from: string): Promise<QboWriteStatus> {
    return parse(QboWriteStatus, await send('/qbo/write/turn-on', { method: 'POST', body: { from } }), 'POST /qbo/write/turn-on');
  },
  async qboWritePause(paused: boolean): Promise<QboWriteStatus> {
    return parse(QboWriteStatus, await send('/qbo/write/pause', { method: 'POST', body: { paused } }), 'POST /qbo/write/pause');
  },
  async qboWriteTurnOff(): Promise<QboWriteStatus> {
    return parse(QboWriteStatus, await send('/qbo/write/turn-off', { method: 'POST', body: {} }), 'POST /qbo/write/turn-off');
  },
  practice: (signal?: AbortSignal) => get(PracticeCounts, '/practice', signal),
  async practiceClear(): Promise<PracticeCounts> {
    return parse(PracticeCounts, await send('/practice/clear', { method: 'POST', body: { confirm: 'DELETE PRACTICE' } }), 'POST /practice/clear');
  },
  async qboWriteDoneThere(id: string): Promise<QboWriteStatus> {
    return parse(QboWriteStatus, await send(`/qbo/outbox/${id}/done-there`, { method: 'POST', body: {} }), 'POST /qbo/outbox/:id/done-there');
  },
  async qboWriteRetry(id: string): Promise<QboWriteStatus> {
    return parse(QboWriteStatus, await send(`/qbo/outbox/${id}/retry`, { method: 'POST', body: {} }), 'POST /qbo/outbox/:id/retry');
  },
  async qboWriteSendNow(): Promise<QboWriteDrained> {
    return parse(QboWriteDrained, await send('/qbo/write/send', { method: 'POST', body: {} }), 'POST /qbo/write/send');
  },
  async qboMirrorRun(): Promise<{ started: true }> {
    return parse(z.object({ started: z.literal(true) }), await send('/qbo/mirror/run', { method: 'POST' }), 'POST /qbo/mirror/run');
  },
  async stopCashReverse(stopId: string): Promise<Route> {
    return parse(Route, await send(`/stops/${stopId}/cash/reverse`, { method: 'POST', body: {} }), 'POST /stops/:id/cash/reverse');
  },
  async qboMatchRun(): Promise<QboMatchRun> {
    return parse(QboMatchRun, await send('/qbo/matching/run', { method: 'POST' }), 'POST /qbo/matching/run');
  },
  async qboLink(body: { entity: QboLinkEntity; qboId: string; appId: string; unit?: QboMatching['linked'][number]['unit'] }): Promise<void> {
    await send('/qbo/links', { method: 'PUT', body });
  },
  async qboUnlink(body: { entity: QboLinkEntity; qboId: string }): Promise<void> {
    await send('/qbo/links/remove', { method: 'POST', body });
  },
  async qboCreateFrom(body: { entity: QboLinkEntity; qboId: string }): Promise<{ appId: string }> {
    return parse(QboLinkCreated, await send('/qbo/links/create', { method: 'POST', body }), 'POST /qbo/links/create');
  },
  async qboConnect(environment: QboEnvironment): Promise<{ url: string }> {
    return parse(QboConnectUrl, await send('/qbo/connect', { method: 'POST', body: { environment } }), 'POST /qbo/connect');
  },
  async qboPull(full = false): Promise<{ started: boolean }> {
    return parse(QboPullStarted, await send(`/qbo/pull${full ? '?full=1' : ''}`, { method: 'POST' }), 'POST /qbo/pull');
  },
  async qboDisconnect(): Promise<QboStatus> {
    return parse(QboStatus, await send('/qbo/disconnect', { method: 'POST' }), 'POST /qbo/disconnect');
  },
  async runJob(name: string): Promise<JobRun> {
    return parse(JobRun, await send(`/system/jobs/${seg(name)}/run`, { method: 'POST' }), 'POST /system/jobs/run');
  },
  payables: (signal?: AbortSignal) => get(Payables, '/payables', signal),
  async payVendor(body: VendorPaymentCreate, idempotencyKey: string): Promise<VendorPayment> {
    return parse(VendorPayment, await send('/vendor-payments', { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST /vendor-payments');
  },
  async reverseVendorPayment(id: string, note: string): Promise<VendorPayment> {
    return parse(VendorPayment, await send(`/vendor-payments/${seg(id)}/reverse`, { method: 'POST', body: { note } }), 'POST /vendor-payments/reverse');
  },
  async cashTurnIn(routeId: string, body: RouteCashTurnIn): Promise<Route> {
    return parse(Route, await send(`/routes/${seg(routeId)}/cash-turn-in`, { method: 'POST', body }), 'POST /routes/cash-turn-in');
  },
  priceWorksheet: (q: string, signal?: AbortSignal) => get(PriceWorksheet, `/pricing/worksheet${qs(q ? { q } : {})}`, signal),
  async saveOpeningBalances(body: OpeningBalancesPut, idempotencyKey: string): Promise<OpeningBalances> {
    return parse(OpeningBalances, await send('/ledger/opening-balances', { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST /ledger/opening-balances');
  },
  async setLedgerStart(balanceFrom: string | null): Promise<LedgerSettings> {
    return parse(LedgerSettings, await send('/ledger/settings', { method: 'PATCH', body: { balanceFrom } }), 'PATCH /ledger/settings');
  },
  async publishPrices(body: PricePublish & { approveFloor?: boolean }, idempotencyKey: string): Promise<PricePublished> {
    return parse(PricePublished, await send('/pricing/publish', { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST /pricing/publish');
  },
  priceList: (q: { tier?: string; customer?: string }, signal?: AbortSignal) => get(PriceList, `/pricing/price-list${qs(q)}`, signal),
  managedUsers: (signal?: AbortSignal) => get(z.array(ManagedUser), '/users/manage', signal),
  async createUser(body: UserCreate): Promise<ManagedUser> {
    return parse(ManagedUser, await send('/users', { method: 'POST', body }), 'POST /users');
  },
  async patchUser(id: string, body: UserPatch): Promise<ManagedUser> {
    return parse(ManagedUser, await send(`/users/${seg(id)}`, { method: 'PATCH', body }), 'PATCH /users');
  },
  /** null when signed out (401). */
  async me(signal?: AbortSignal): Promise<Me | null> {
    try {
      return await get(Me, '/auth/me', signal);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return null;
      throw e;
    }
  },
  async logout(): Promise<void> {
    await send('/auth/logout', { method: 'POST' });
  },
  items: (q: string, signal?: AbortSignal) => get(ItemList, `/items${qs({ q })}`, signal),
  item: (id: string, signal?: AbortSignal) => get(Item, `/items/${seg(id)}`, signal),
  itemCatalog: (id: string, signal?: AbortSignal) => get(ItemCatalog, `/items/${seg(id)}/catalog`, signal),
  itemProfile: (id: string, signal?: AbortSignal) => get(ItemProfile, `/items/${seg(id)}/profile`, signal),
  async saveItemProfile(id: string, body: ItemProfilePut): Promise<ItemProfile> {
    return parse(ItemProfile, await send(`/items/${seg(id)}/profile`, { method: 'PUT', body }), 'PUT profile');
  },
  async createItem(body: ItemCreate): Promise<Item> {
    return parse(Item, await send('/items', { method: 'POST', body }), 'POST /items');
  },
  async patchItem(id: string, body: ItemPatch): Promise<Item> {
    return parse(Item, await send(`/items/${seg(id)}`, { method: 'PATCH', body }), `PATCH /items/${id}`);
  },
  async addAlias(id: string, body: ItemAliasCreate): Promise<ItemAlias> {
    return parse(ItemAlias, await send(`/items/${seg(id)}/aliases`, { method: 'POST', body }), 'POST alias');
  },
  async removeAlias(id: string, aliasId: string): Promise<void> {
    await send(`/items/${seg(id)}/aliases/${seg(aliasId)}`, { method: 'DELETE' });
  },
  unknownNames: (signal?: AbortSignal) => get(z.array(UnknownName), '/catalog/unknown-names', signal),
  assistantStatus: (signal?: AbortSignal) => get(AssistantStatus, '/assistant', signal),
  async askClaude(body: AssistantAsk, signal?: AbortSignal): Promise<AssistantAnswer> {
    return parse(AssistantAnswer, await send('/assistant', { method: 'POST', body, timeoutMs: 170_000, ...(signal ? { signal } : {}) }), 'POST /assistant');
  },
  async pasteToClaude(body: AssistantPaste): Promise<AssistantPasteAnswer> {
    return parse(AssistantPasteAnswer, await send('/assistant/paste', { method: 'POST', body, timeoutMs: 170_000 }), 'POST /assistant/paste');
  },
  async suggestUnknownNames(body: UnknownNameSuggest): Promise<UnknownNameSuggestions> {
    return parse(UnknownNameSuggestions, await send('/catalog/unknown-names/suggest', { method: 'POST', body }), 'POST suggest');
  },
  async assignUnknownName(body: UnknownNameAssign): Promise<UnknownNameAssigned> {
    return parse(UnknownNameAssigned, await send('/catalog/unknown-names/assign', { method: 'POST', body }), 'POST assign');
  },
  customers: (q: string, signal?: AbortSignal, stage?: CustomerStage, archived?: boolean) => get(CustomerList, `/customers${qs({ q, stage, ...(archived ? { archived: 'true' } : {}) })}`, signal),
  async createCustomer(body: CustomerCreate): Promise<Customer> {
    return parse(Customer, await send('/customers', { method: 'POST', body }), 'POST /customers');
  },
  async archiveCustomer(id: string, archive: boolean): Promise<Customer> {
    return parse(Customer, await send(`/customers/${seg(id)}/${archive ? 'archive' : 'restore'}`, { method: 'POST' }), 'POST archive');
  },
  async putCustomerLocation(id: string, body: CustomerLocationPut): Promise<Customer> {
    return parse(Customer, await send(`/customers/${seg(id)}/location`, { method: 'PUT', body }), 'PUT location');
  },
  stopSignatureUrl: (stopId: string) => `${API_BASE}/stops/${seg(stopId)}/signature`,
  async patchCustomer(id: string, body: CustomerPatch): Promise<Customer> {
    return parse(Customer, await send(`/customers/${seg(id)}`, { method: 'PATCH', body }), `PATCH /customers/${id}`);
  },
  // OAuth consent (Claude.ai connectors)
  oauthClient: (clientId: string, signal?: AbortSignal) => get(OAuthClientInfo, `/oauth/client/${seg(clientId)}`, signal),
  async oauthApprove(body: OAuthApprove): Promise<OAuthApproved> {
    return parse(OAuthApproved, await send('/oauth/approve', { method: 'POST', body }), 'POST approve');
  },
  // routes
  routes: (date: string, signal?: AbortSignal) => get(z.array(Route), `/routes${qs({ date })}`, signal),
  async buildRoute(date: string): Promise<Route[]> {
    return parse(z.array(Route), await send('/routes/build', { method: 'POST', body: { date } }), 'POST /routes/build');
  },
  async createRoute(date: string, name: string): Promise<Route> {
    return parse(Route, await send('/routes', { method: 'POST', body: { date, name } }), 'POST /routes');
  },
  async moveStop(stopId: string, routeId: string): Promise<Route[]> {
    return parse(z.array(Route), await send(`/stops/${seg(stopId)}/move`, { method: 'POST', body: { routeId } }), 'POST move');
  },
  async patchRoute(id: string, body: RoutePatch): Promise<Route> {
    return parse(Route, await send(`/routes/${seg(id)}`, { method: 'PATCH', body }), 'PATCH route');
  },
  async startRoute(id: string): Promise<Route> {
    return parse(Route, await send(`/routes/${seg(id)}/start`, { method: 'POST' }), 'POST start');
  },
  async arriveStop(id: string): Promise<Route> {
    return parse(Route, await send(`/stops/${seg(id)}/arrive`, { method: 'POST' }), 'POST arrive');
  },
  async deliverStop(id: string, body: StopDeliver): Promise<Route> {
    return parse(Route, await send(`/stops/${seg(id)}/deliver`, { method: 'POST', body }), 'POST deliver');
  },
  async notDeliverStop(id: string, body: StopNotDelivered): Promise<Route> {
    return parse(Route, await send(`/stops/${seg(id)}/not-delivered`, { method: 'POST', body }), 'POST not-delivered');
  },
  async stopAnotherDay(id: string, body: StopAnotherDay): Promise<Route[]> {
    return parse(z.array(Route), await send(`/stops/${seg(id)}/another-day`, { method: 'POST', body }), 'POST another-day');
  },
  async finishRoute(id: string): Promise<Route> {
    return parse(Route, await send(`/routes/${seg(id)}/finish`, { method: 'POST' }), 'POST finish');
  },
  routeDrivers: (signal?: AbortSignal) => get(z.array(RouteDriver), '/routes/drivers', signal),
  async etaText(id: string, minutes: number): Promise<Route> {
    return parse(Route, await send(`/stops/${seg(id)}/eta-text`, { method: 'POST', body: { minutes } }), 'POST eta');
  },
  async stopPhoto(id: string, file: File): Promise<void> {
    const form = new FormData();
    form.append('file', file, file.name);
    const res = await fetch(`${API_BASE}/stops/${seg(id)}/photo`, { method: 'POST', credentials: 'include', body: form, headers: { Accept: 'application/json' } });
    if (!res.ok) {
      const data: unknown = await res.json().catch(() => null);
      throw new ApiError(Problem.safeParse(data).success ? Problem.parse(data) : { status: res.status, title: res.statusText || 'Error' });
    }
  },
  // messages
  messagingStatus: (signal?: AbortSignal) => get(MessagingStatus, '/messages/status', signal),
  messageInbox: (signal?: AbortSignal) => get(z.array(Message), '/messages/inbox', signal),
  messageThread: (customerId: string, signal?: AbortSignal) => get(z.array(Message), `/messages${qs({ customer: customerId })}`, signal),
  async sendMessage(body: MessageSend): Promise<Message> {
    return parse(Message, await send('/messages/send', { method: 'POST', body }), 'POST /messages/send');
  },
  async messageHandled(id: string): Promise<Message> {
    return parse(Message, await send(`/messages/${seg(id)}/handled`, { method: 'POST' }), 'POST handled');
  },
  async messageToDraft(id: string): Promise<Order> {
    return parse(Order, await send(`/messages/${seg(id)}/draft`, { method: 'POST' }), 'POST draft');
  },
  async textOrderConfirmation(orderId: string): Promise<Message> {
    return parse(Message, await send('/messages/order-confirmation', { method: 'POST', body: { orderId } }), 'POST confirmation');
  },
  audience: (q: { item?: string; days?: number }, signal?: AbortSignal) => get(z.array(AudienceMember), `/messages/audience${qs({ item: q.item, days: q.days === undefined ? undefined : String(q.days) })}`, signal),
  async blastPreview(body: BlastRequest): Promise<BlastPreview> {
    return parse(BlastPreview, await send('/messages/blast/preview', { method: 'POST', body }), 'POST preview');
  },
  async blast(body: BlastRequest): Promise<BlastResult> {
    return parse(BlastResult, await send('/messages/blast', { method: 'POST', body }), 'POST blast');
  },
  // the customer portal
  portalHome: (signal?: AbortSignal) => get(PortalHome, '/portal/home', signal),
  portalCatalog: (signal?: AbortSignal) => get(z.array(PortalCatalogItem), '/portal/catalog', signal),
  portalDates: (signal?: AbortSignal) => get(z.array(z.string()), '/portal/dates', signal),
  portalOrders: (signal?: AbortSignal) => get(z.array(Order), '/portal/orders', signal),
  portalOrder: (id: string, signal?: AbortSignal) => get(Order, `/portal/orders/${seg(id)}`, signal),
  portalStatement: (signal?: AbortSignal) => get(Statement, '/portal/statement', signal),
  portalInvoices: (signal?: AbortSignal) => get(z.array(PortalInvoiceRow), '/portal/invoices', signal),
  portalInvoice: (id: string, signal?: AbortSignal) => get(PublicInvoice, `/portal/invoices/${seg(id)}`, signal),
  async cancelPortalOrder(id: string): Promise<Order> {
    return parse(Order, await send(`/portal/orders/${seg(id)}/cancel`, { method: 'POST' }), 'POST cancel');
  },
  portalSettings: (signal?: AbortSignal) => get(PortalSettings, '/settings/portal', signal),
  async savePortalSettings(body: PortalSettings): Promise<PortalSettings> {
    return parse(PortalSettings, await send('/settings/portal', { method: 'PATCH', body }), 'PATCH /settings/portal');
  },
  async placePortalOrder(body: PortalOrderCreate, idempotencyKey: string): Promise<Order> {
    return parse(Order, await send('/portal/orders', { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST /portal/orders');
  },
  // CRM
  followups: (date: string, signal?: AbortSignal) => get(Followups, `/crm/followups${qs({ date })}`, signal),
  crmTrends: (date: string, signal?: AbortSignal) => get(CrmTrends, `/crm/trends${qs({ date })}`, signal),
  itemUsda: (itemId: string, signal?: AbortSignal) => get(ItemUsda, `/items/${seg(itemId)}/usda`, signal),
  usdaCommodities: (q: string, signal?: AbortSignal) => get(z.array(UsdaCommodity), `/usda/commodities${qs({ q })}`, signal),
  async linkUsda(itemId: string, body: UsdaLink) {
    return parse(ItemUsda, await send(`/items/${seg(itemId)}/usda`, { method: 'PUT', body }), 'PUT usda');
  },
  async unlinkUsda(itemId: string) {
    await send(`/items/${seg(itemId)}/usda`, { method: 'DELETE' });
  },
  // contacts: the By day tab's call list (decision 168)
  forecastAhead: (from: string, to: string, signal?: AbortSignal) => get(ForecastAhead, `/forecast/ahead${qs({ from, to, contacts: 'true' })}`, signal),
  activities: (customerId: string, signal?: AbortSignal) => get(z.array(Activity), `/customers/${seg(customerId)}/activities`, signal),
  async addActivity(customerId: string, body: ActivityCreate): Promise<Activity> {
    return parse(Activity, await send(`/customers/${seg(customerId)}/activities`, { method: 'POST', body }), 'POST activity');
  },
  async setActivityDone(id: string, done: boolean): Promise<Activity> {
    return parse(Activity, await send(`/activities/${seg(id)}`, { method: 'PATCH', body: { done } }), 'PATCH activity');
  },
  async addContact(customerId: string, body: ContactCreate): Promise<Contact> {
    return parse(Contact, await send(`/customers/${seg(customerId)}/contacts`, { method: 'POST', body }), 'POST contact');
  },
  async patchContact(id: string, body: ContactPatch): Promise<Contact> {
    return parse(Contact, await send(`/contacts/${seg(id)}`, { method: 'PATCH', body }), 'PATCH contact');
  },
  customer: (id: string, signal?: AbortSignal) => get(Customer, `/customers/${seg(id)}`, signal),
  priceSheet: (id: string, signal?: AbortSignal) => get(z.array(PriceSheetRow), `/customers/${seg(id)}/price-sheet`, signal),
  async createInvoice(body: InvoiceCreate, idempotencyKey: string): Promise<Invoice> {
    return parse(Invoice, await send('/invoices', { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST /invoices');
  },
  orders: (date: string, signal?: AbortSignal) => get(OrderList, `/orders${qs({ date })}`, signal),
  // C6: find an order by the customer's name or its number, any day
  findOrders: (q: string, signal?: AbortSignal) => get(OrderList, `/orders${qs({ q })}`, signal),
  ordersByStatus: (status: Order['status'], signal?: AbortSignal) => get(OrderList, `/orders${qs({ status })}`, signal),
  // office worklists (decision 108)
  collections: (signal?: AbortSignal) => get(z.array(CollectionRow), '/work/collections', signal),
  complaints: (signal?: AbortSignal) => get(z.array(ComplaintRow), '/work/complaints', signal),
  noCost: (signal?: AbortSignal) => get(z.array(NoCostRow), '/work/no-cost', signal),
  async addPromise(customerId: string, body: PromiseCreate, idempotencyKey: string): Promise<PaymentPromise> {
    return parse(PaymentPromise, await send(`/customers/${seg(customerId)}/promises`, { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST promise');
  },
  /** GET /orders/:id is not in the endpoint list yet; the detail screen falls back to cached orders. */
  order: (id: string, signal?: AbortSignal) => get(Order, `/orders/${seg(id)}`, signal),
  /** C6: an order changed before it is picked (lines, date, notes), with the version it was opened at */
  async updateOrder(id: string, body: OrderRevise): Promise<Order> {
    return parse(Order, await send(`/orders/${seg(id)}`, { method: 'PUT', body }), 'PUT /orders/:id');
  },
  /** C6: the prices saving would charge this customer, and which need the owner */
  async orderQuote(body: OrderQuoteAsk, signal?: AbortSignal): Promise<OrderQuote> {
    return parse(OrderQuote, await send('/orders/quote', { method: 'POST', body, signal }), 'POST /orders/quote');
  },
  async createOrder(body: OrderCreate, idempotencyKey: string): Promise<Order> {
    const data = await send('/orders', { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } });
    return parse(Order, data, 'POST /orders');
  },
  priceTiers: (signal?: AbortSignal) => get(z.array(PriceTier), '/price-tiers', signal),
  // warehouse
  vendors: (signal?: AbortSignal) => get(z.array(VendorSummary), '/vendors', signal),
  vendorBook: (archived: boolean, signal?: AbortSignal) => get(z.array(Vendor), `/vendors/book${archived ? '?archived=true' : ''}`, signal),
  async createVendor(body: VendorCreate): Promise<Vendor> {
    return parse(Vendor, await send('/vendors', { method: 'POST', body }), 'POST /vendors');
  },
  async patchVendor(id: string, body: VendorPatch): Promise<Vendor> {
    return parse(Vendor, await send(`/vendors/${seg(id)}`, { method: 'PATCH', body }), 'PATCH vendor');
  },
  async archiveVendor(id: string, archive: boolean): Promise<Vendor> {
    return parse(Vendor, await send(`/vendors/${seg(id)}/${archive ? 'archive' : 'restore'}`, { method: 'POST' }), 'POST vendor archive');
  },
  // Products → Vendor codes (decision 150)
  vendorCodes: (vendorId: string, signal?: AbortSignal) => get(z.array(VendorCodeRow), `/vendors/${seg(vendorId)}/codes`, signal),
  async changeVendorCode(vendorId: string, codeId: string, body: { itemId?: string | null; hidden?: boolean; from?: string | null }): Promise<z.infer<typeof VendorCodeChanged>> {
    return parse(VendorCodeChanged, await send(`/vendors/${seg(vendorId)}/codes/${seg(codeId)}`, { method: 'PUT', body }), 'PUT vendor code');
  },
  async addVendorCode(vendorId: string, body: { vendorCode: string; vendorDesc?: string; itemId: string }): Promise<z.infer<typeof VendorCodeChanged>> {
    return parse(VendorCodeChanged, await send(`/vendors/${seg(vendorId)}/codes`, { method: 'POST', body }), 'POST vendor code');
  },
  receipts: (q: { vendor?: string; from?: string; to?: string }, signal?: AbortSignal) => get(z.array(ReceiptSummary), `/receipts${qs(q)}`, signal),
  receipt: (id: string, signal?: AbortSignal) => get(Receipt, `/receipts/${seg(id)}`, signal),
  async receive(body: ReceiptCreate, idempotencyKey: string): Promise<Receipt> {
    return parse(Receipt, await send('/receipts', { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST /receipts');
  },
  lots: (q: { item?: string; location?: string; site?: string; all?: string }, signal?: AbortSignal) => get(z.array(Lot), `/stock/lots${qs(q)}`, signal),
  async shrink(body: ShrinkCreate, idempotencyKey: string): Promise<ShrinkResult> {
    return parse(ShrinkResult, await send('/stock/shrink', { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST /stock/shrink');
  },
  pickList: (date: string, site?: string | null, signal?: AbortSignal) => get(PickList, `/pick-list${qs({ date, ...(site ? { site } : {}) })}`, signal),
  // the prep card (decision 179): the invoices dated from..to as the fillers build them; marked printed with what was printed
  prep: (from: string, to: string, signal?: AbortSignal) => get(PrepList, `/prep${qs({ from, to })}`, signal),
  async prepPrinted(body: PrepPrinted): Promise<void> {
    await send('/prep/printed', { method: 'POST', body });
  },
  async setOrderStatus(id: string, status: Order['status'], reason?: string, overrideHold?: boolean): Promise<Order> {
    return parse(Order, await send(`/orders/${seg(id)}/status`, { method: 'PATCH', body: { status, ...(reason ? { reason } : {}), ...(overrideHold ? { overrideHold: true } : {}) } }), 'PATCH status');
  },
  profit: (q: { by: 'customer' | 'item'; from: string; to: string }, signal?: AbortSignal) => get(ProfitReport, `/reports/profit${qs(q)}`, signal),
  weather: (days: number, signal?: AbortSignal) => get(z.array(WeatherDay), `/weather${qs({ days: String(days) })}`, signal),
  // compliance, food safety and the fleet (decision 112)
  renewals: (signal?: AbortSignal) => get(z.array(Renewal), '/renewals', signal),
  async saveRenewal(id: string | null, body: RenewalPut, idempotencyKey: string): Promise<Renewal> {
    return parse(Renewal, await send(id ? `/renewals/${seg(id)}` : '/renewals', { method: id ? 'PUT' : 'POST', body, ...(id ? {} : { headers: { 'Idempotency-Key': idempotencyKey } }) }), 'save renewal');
  },
  async archiveRenewal(id: string): Promise<void> {
    await send(`/renewals/${seg(id)}/archive`, { method: 'POST' });
  },
  temperatures: (q: { from?: string; to?: string }, signal?: AbortSignal) => get(z.array(Temperature), `/temperatures${qs(q)}`, signal),
  async logTemperature(body: TemperatureCreate, idempotencyKey: string): Promise<Temperature> {
    return parse(Temperature, await send('/temperatures', { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST /temperatures');
  },
  async voidTemperature(id: string, note: string): Promise<Temperature> {
    return parse(Temperature, await send(`/temperatures/${seg(id)}/void`, { method: 'POST', body: { note } }), 'void reading');
  },
  async saveFoodSafety(body: FoodSafetySettings): Promise<FoodSafetySettings> {
    return parse(FoodSafetySettings, await send('/settings/food-safety', { method: 'PATCH', body }), 'PATCH food safety');
  },
  foodSafety: (signal?: AbortSignal) => get(FoodSafetySettings, '/settings/food-safety', signal),
  vehicles: (signal?: AbortSignal) => get(z.array(Vehicle), '/vehicles', signal),
  async addVehicle(body: VehicleCreate): Promise<Vehicle> {
    return parse(Vehicle, await send('/vehicles', { method: 'POST', body }), 'POST /vehicles');
  },
  async patchVehicle(id: string, body: VehiclePatch): Promise<Vehicle> {
    return parse(Vehicle, await send(`/vehicles/${seg(id)}`, { method: 'PATCH', body }), 'PATCH vehicle');
  },
  vehicleLogs: (id: string, signal?: AbortSignal) => get(z.array(VehicleLog), `/vehicles/${seg(id)}/logs`, signal),
  async logVehicle(id: string, body: VehicleLogCreate, idempotencyKey: string): Promise<VehicleLog> {
    return parse(VehicleLog, await send(`/vehicles/${seg(id)}/logs`, { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST vehicle log');
  },
  async voidVehicleLog(id: string, note: string): Promise<VehicleLog> {
    return parse(VehicleLog, await send(`/vehicle-logs/${seg(id)}/void`, { method: 'POST', body: { note } }), 'void vehicle log');
  },
  // margin tools (decision 111)
  priceChanges: (from: string, to: string, signal?: AbortSignal) => get(PriceChanges, `/reports/price-changes${qs({ from, to })}`, signal),
  // Reports → Costs and prices (decision 163)
  // a year of sales with their costs can take longer than the usual 20 seconds: up to the server's own limit
  async costDash(p: { from: string; to: string; vendor?: string }, signal?: AbortSignal) {
    const path = `/reports/costs-prices${qs(p)}`;
    return parse(CostDash, await send(path, { timeoutMs: 60_000, ...(signal ? { signal } : {}) }), `GET ${path}`);
  },
  costDashItem: (id: string, p: { vendor?: string; tier?: string }, signal?: AbortSignal) => get(CostDashItem, `/reports/costs-prices/items/${seg(id)}${qs(p)}`, signal),
  /** Administration → Activity (decision 167): who did what, newest first; owner only */
  activity: (p: { from: string; to: string; user?: string; area?: string; system?: 'true'; before?: string }, signal?: AbortSignal) => get(ActivityPage, `/activity${qs(p)}`, signal),
  itemCategories: (signal?: AbortSignal) => get(z.array(ItemCategory), '/items/categories', signal),
  stalePrices: (threshold: number, signal?: AbortSignal) => get(z.array(StalePrice), `/reports/stale-prices${qs({ threshold: String(threshold) })}`, signal),
  suggestions: (customerId: string, signal?: AbortSignal) => get(z.array(Suggestion), `/customers/${seg(customerId)}/suggestions`, signal),
  marginSettings: (signal?: AbortSignal) => get(MarginSettings, '/settings/margin', signal),
  async saveMarginSettings(body: MarginSettings): Promise<MarginSettings> {
    return parse(MarginSettings, await send('/settings/margin', { method: 'PATCH', body }), 'PATCH /settings/margin');
  },
  aging: (signal?: AbortSignal) => get(AgingReport, '/reports/aging', signal),
  customerMetrics: (signal?: AbortSignal) => get(CustomerMetrics, '/reports/customer-metrics', signal),
  openingBalances: (signal?: AbortSignal) => get(OpeningBalances, '/ledger/opening-balances', signal),
  pacaSettings: (signal?: AbortSignal) => get(PacaSettings, '/paca/settings', signal),
  async setPacaSettings(body: PacaSettings): Promise<PacaSettings> {
    return parse(PacaSettings, await send('/paca/settings', { method: 'PATCH', body }), 'PATCH /paca/settings');
  },
  companySettings: (signal?: AbortSignal) => get(CompanySettings, '/settings/company', signal),
  async setCompanySettings(body: CompanySettings): Promise<CompanySettings> {
    return parse(CompanySettings, await send('/settings/company', { method: 'PATCH', body }), 'PATCH /settings/company');
  },
  pacaLetter: (customerId: string, signal?: AbortSignal) => get(PacaLetter, `/customers/${seg(customerId)}/paca-letter`, signal),
  async recordPacaNotice(customerId: string, body: { kind: 'demand' | 'trust_notice'; body: string }, idempotencyKey: string): Promise<PacaNotice> {
    return parse(PacaNotice, await send(`/customers/${seg(customerId)}/paca-notices`, { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST paca-notices');
  },
  taxSettings: (signal?: AbortSignal) => get(TaxSettings, '/tax/settings', signal),
  exceptions: (q: { from: string; to: string }, signal?: AbortSignal) => get(ExceptionsReport, `/reports/exceptions${qs(q)}`, signal),
  // standing orders
  standingOrders: (customerId: string, signal?: AbortSignal) => get(z.array(StandingOrder), `/standing-orders${qs({ customer: customerId })}`, signal),
  async putStandingOrder(customerId: string, body: StandingOrderPut): Promise<StandingOrder> {
    return parse(StandingOrder, await send(`/customers/${seg(customerId)}/standing-order`, { method: 'PUT', body }), 'PUT standing');
  },
  async deleteStandingOrder(customerId: string): Promise<void> {
    await send(`/customers/${seg(customerId)}/standing-order`, { method: 'DELETE' });
  },
  standingDue: (date: string, signal?: AbortSignal) => get(z.object({ date: z.string(), due: z.number(), drafted: z.number() }), `/standing-orders/due${qs({ date })}`, signal),
  async runStanding(date: string, openDaysOnly = false): Promise<StandingRun> {
    return parse(StandingRun, await send('/standing-orders/run', { method: 'POST', body: openDaysOnly ? { date, openDaysOnly } : { date } }), 'POST run');
  },
  // price intelligence
  intel: (itemId: string, signal?: AbortSignal) => get(ItemIntel, `/items/${seg(itemId)}/intel`, signal),
  priceHistory: (itemId: string, q: { from: string; to: string }, signal?: AbortSignal) =>
    get(ItemPriceHistory, `/items/${seg(itemId)}/price-history?${new URLSearchParams(q).toString()}`, signal),
  competitors: (signal?: AbortSignal) => get(z.array(Competitor), '/competitors', signal),
  async recordCompetitorPrice(itemId: string, body: CompetitorPriceCreate): Promise<CompetitorPrice> {
    return parse(CompetitorPrice, await send(`/items/${seg(itemId)}/competitor-prices`, { method: 'POST', body }), 'POST competitor price');
  },
  // vendor documents (costs)
  vendorDocs: (q: { vendor?: string; from?: string; to?: string; type?: string; kind?: 'bills' | 'lists' }, signal?: AbortSignal) => get(z.array(VendorDocSummary), `/vendor-documents${qs(q)}`, signal),
  vendorDoc: (id: string, signal?: AbortSignal) => get(VendorDoc, `/vendor-documents/${seg(id)}`, signal),
  async createVendorDoc(body: VendorDocCreate): Promise<VendorDoc> {
    return parse(VendorDoc, await send('/vendor-documents', { method: 'POST', body }), 'POST /vendor-documents');
  },
  readerStatus: (signal?: AbortSignal) => get(z.object({ configured: z.boolean(), model: z.string() }), '/vendor-documents/reader', signal),
  /** The AI reader: the photo or PDF becomes a draft of the form. Nothing is saved until the person saves. */
  async readVendorDoc(file: File, vendorId: string): Promise<VendorDocDraft> {
    const form = new FormData();
    if (vendorId) form.append('vendorId', vendorId);
    form.append('file', file, file.name);
    const res = await fetch(`${API_BASE}/vendor-documents/read`, { method: 'POST', credentials: 'include', body: form, headers: { Accept: 'application/json' } });
    const text = await res.text().catch(() => '');
    const data = jsonOrNull(text);
    if (!res.ok) throw new ApiError(Problem.safeParse(data).success ? Problem.parse(data) : uploadFailed(res, true));
    return parse(VendorDocDraft, data, 'POST read');
  },
  async attachVendorDocFile(id: string, file: File): Promise<FileInfo> {
    const form = new FormData();
    form.append('file', file, file.name);
    const res = await fetch(`${API_BASE}/vendor-documents/${seg(id)}/file`, { method: 'POST', credentials: 'include', body: form, headers: { Accept: 'application/json' } });
    const text = await res.text().catch(() => '');
    const data = jsonOrNull(text);
    if (!res.ok) throw new ApiError(Problem.safeParse(data).success ? Problem.parse(data) : uploadFailed(res));
    return parse(FileInfo, data, 'POST file');
  },
  // vendor papers read in the background (decision 146)
  papers: (status: string | undefined, offset: number, signal?: AbortSignal) => get(PaperList, `/papers${qs({ ...(status ? { status } : {}), ...(offset ? { offset: String(offset) } : {}) })}`, signal),
  async addPaper(file: File): Promise<{ id: string; duplicate: boolean }> {
    const form = new FormData();
    form.append('file', file, file.name);
    const res = await fetch(`${API_BASE}/papers`, { method: 'POST', credentials: 'include', body: form, headers: { Accept: 'application/json' } });
    const text = await res.text().catch(() => '');
    const data = jsonOrNull(text);
    if (!res.ok) throw new ApiError(Problem.safeParse(data).success ? Problem.parse(data) : uploadFailed(res));
    return parse(PaperAdded, data, 'POST /papers');
  },
  // old mail from a Google Takeout file (decision 148): one paper of one mail, with the mail's sender and Google's check
  async addMailPaper(mail: { mailId: string; index: number; receivedAt: string; from: string | null; auth: string | null; nested: boolean }, paper: { filename: string; mimeType: string; body: Uint8Array }, signal?: AbortSignal): Promise<{ id: string; duplicate: boolean }> {
    const form = new FormData();
    form.append('mailId', mail.mailId);
    form.append('index', String(mail.index));
    form.append('receivedAt', mail.receivedAt);
    if (mail.from) form.append('from', mail.from.slice(0, 1000));
    if (mail.auth) form.append('auth', mail.auth.slice(0, 8000));
    form.append('nested', mail.nested ? '1' : '0');
    form.append('file', new Blob([paper.body as BlobPart], { type: paper.mimeType }), paper.filename);
    const res = await fetch(`${API_BASE}/papers/mail`, { method: 'POST', credentials: 'include', body: form, headers: { Accept: 'application/json' }, ...(signal ? { signal } : {}) });
    const text = await res.text().catch(() => '');
    const data = jsonOrNull(text);
    if (!res.ok) throw new ApiError(Problem.safeParse(data).success ? Problem.parse(data) : uploadFailed(res));
    return parse(PaperAdded, data, 'POST /papers/mail');
  },
  async mailPapersKnown(refs: string[]): Promise<string[]> {
    return parse(MailPapersKnownResult, await send('/papers/mail/known', { method: 'POST', body: { refs } }), 'POST /papers/mail/known').known;
  },
  retryPaper: (id: string) => send(`/papers/${seg(id)}/retry`, { method: 'POST' }),
  async recheckPapers(ids?: string[]): Promise<z.infer<typeof PaperKeepResult>> {
    return parse(PaperKeepResult, await send('/papers/recheck', { method: 'POST', body: ids ? { ids } : {} }), 'POST /papers/recheck');
  },
  async nameVendor(name: string, vendorId: string): Promise<z.infer<typeof PaperKeepResult>> {
    return parse(PaperKeepResult, await send('/papers/vendor-name', { method: 'POST', body: { name, vendorId } }), 'POST /papers/vendor-name');
  },
  async keepPapers(ids: string[]): Promise<z.infer<typeof PaperKeepResult>> {
    return parse(PaperKeepResult, await send('/papers/keep', { method: 'POST', body: { ids } }), 'POST /papers/keep');
  },
  retryFailedPapers: () => send('/papers/retry-failed', { method: 'POST' }),
  gmailStatus: (signal?: AbortSignal) => get(GmailStatus, '/gmail/status', signal),
  async gmailConnect(): Promise<{ url: string }> {
    return parse(QboConnectUrl, await send('/gmail/connect', { method: 'POST' }), 'POST /gmail/connect');
  },
  async gmailDisconnect(): Promise<GmailStatus> {
    return parse(GmailStatus, await send('/gmail/disconnect', { method: 'POST' }), 'POST /gmail/disconnect');
  },
  async gmailTrusted(domains: string[]): Promise<GmailStatus> {
    return parse(GmailStatus, await send('/gmail/trusted', { method: 'PUT', body: { domains } }), 'PUT /gmail/trusted');
  },
  trustPaper: (id: string) => send(`/papers/${seg(id)}/trust`, { method: 'POST' }),
  async gmailRead(): Promise<{ ok: boolean; detail: string | null }> {
    return parse(GmailRead, await send('/gmail/read', { method: 'POST' }), 'POST /gmail/read');
  },
  pausePapers: (paused: boolean) => send('/papers/pause', { method: 'POST', body: { paused } }),
  paperFileUrl: (id: string) => `${API_BASE}/papers/${seg(id)}/file`,
  // the linking sheet (decision 157): the Excel file of everything not linked, read back and applied 50 rows at a time
  async linkingSheet(): Promise<{ blob: Blob; filename: string }> {
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/linking/sheet`, { credentials: 'include', signal: withTimeout(undefined, 60_000) ?? null });
    } catch (err) {
      throw new ApiError({ status: 0, title: 'Network error', detail: err instanceof Error ? err.message : String(err) });
    }
    if (!res.ok) {
      const data = jsonOrNull(await res.text().catch(() => ''));
      throw new ApiError(Problem.safeParse(data).success ? Problem.parse(data) : uploadFailed(res));
    }
    const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'morales-linking.xlsx';
    return { blob: await res.blob(), filename: name };
  },
  async linkingPreview(file: File): Promise<LinkingPreview> {
    const form = new FormData();
    form.append('file', file, file.name);
    const res = await fetch(`${API_BASE}/linking/preview`, { method: 'POST', credentials: 'include', body: form, headers: { Accept: 'application/json' } });
    const data = jsonOrNull(await res.text().catch(() => ''));
    if (!res.ok) throw new ApiError(Problem.safeParse(data).success ? Problem.parse(data) : uploadFailed(res));
    return parse(LinkingPreview, data, 'POST /linking/preview');
  },
  async linkingApply(rows: LinkingApply['rows']): Promise<LinkingApplied> {
    return parse(LinkingApplied, await send('/linking/apply', { method: 'POST', body: { rows }, timeoutMs: 60_000 }), 'POST /linking/apply');
  },
  // the register's categories (decision 159)
  cloverCategories: (signal?: AbortSignal) => get(CloverCategories, '/clover/categories', signal),
  async saveCloverFilter(body: { keepAll: boolean; categories: string[]; uncategorized: boolean }): Promise<z.infer<typeof CloverFilterSaved>> {
    return parse(CloverFilterSaved, await send('/clover/filter', { method: 'PUT', body, timeoutMs: 60_000 }), 'PUT /clover/filter');
  },
  linkingIgnored: (signal?: AbortSignal) => get(LinkingIgnored, '/linking/ignored', signal),
  async linkingUnignore(kind: 'qbo_item' | 'name', ref: string): Promise<LinkingIgnored> {
    return parse(LinkingIgnored, await send('/linking/ignored/remove', { method: 'POST', body: { kind, ref } }), 'POST /linking/ignored/remove');
  },
  // buying
  purchaseOrders: (signal?: AbortSignal) => get(z.array(PurchaseOrder), '/purchase-orders', signal),
  async createPurchaseOrder(body: PurchaseOrderCreate): Promise<PurchaseOrder> {
    return parse(PurchaseOrder, await send('/purchase-orders', { method: 'POST', body }), 'POST /purchase-orders');
  },
  dashboard: (signal?: AbortSignal) => get(Dashboard, '/dashboard', signal),
  signals: (signal?: AbortSignal) => get(Signals, '/reports/signals', signal),
  sales: (q: { from: string; to: string; grain?: SalesGrain }, signal?: AbortSignal) => get(SalesAnalysis, `/reports/sales${qs(q)}`, signal),
  emailStatus: (signal?: AbortSignal) => get(EmailStatus, '/email/status', signal),
  async saveEmailSettings(body: EmailSettings): Promise<EmailStatus> {
    return parse(EmailStatus, await send('/settings/email', { method: 'PUT', body }), 'PUT /settings/email');
  },
  invoiceEmails: (id: string, signal?: AbortSignal) => get(z.array(EmailLogEntry), `/invoices/${seg(id)}/emails`, signal),
  /** the invoice's email as the customer would get it; nothing is sent (decision 171) */
  async emailPreview(id: string, body: EmailPreviewAsk, signal?: AbortSignal): Promise<EmailPreview> {
    return parse(EmailPreview, await send(`/invoices/${seg(id)}/email/preview`, { method: 'POST', body, ...(signal ? { signal } : {}) }), 'POST /invoices/email/preview');
  },
  customerEmails: (id: string, signal?: AbortSignal) => get(z.array(EmailLogEntry), `/customers/${seg(id)}/emails`, signal),
  async revokeDocumentLink(id: string): Promise<void> {
    await send(`/document-links/${seg(id)}/revoke`, { method: 'POST' });
  },
  async emailDocument(target: { kind: 'invoice'; id: string } | { kind: 'statement'; customerId: string }, body: DocumentEmailSend, idempotencyKey: string): Promise<z.infer<typeof EmailLogEntry>> {
    const path = target.kind === 'invoice' ? `/invoices/${seg(target.id)}/email` : `/customers/${seg(target.customerId)}/statement/email`;
    return parse(EmailLogEntry, await send(path, { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), `POST ${path}`);
  },
  async publicPay(token: string): Promise<z.infer<typeof PayLink>> {
    return parse(PayLink, await send(`/public/documents/${seg(token)}/pay`, { method: 'POST' }), 'POST /public/documents/pay');
  },
  squareStatus: (signal?: AbortSignal) => get(SquareStatus, '/square/status', signal),
  async saveSquareSettings(live: boolean): Promise<z.infer<typeof SquareStatus>> {
    return parse(SquareStatus, await send('/settings/square', { method: 'PUT', body: { live } }), 'PUT /settings/square');
  },
  async squareTestLink(idempotencyKey: string): Promise<z.infer<typeof PayLink>> {
    return parse(PayLink, await send('/square/test-link', { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey } }), 'POST /square/test-link');
  },
  publicDocument: (token: string, signal?: AbortSignal) => get(z.discriminatedUnion('kind', [PublicInvoice, PublicStatement]), `/public/documents/${seg(token)}`, signal),
  workload: (q: { from: string; to: string }, signal?: AbortSignal) => get(Workload, `/reports/workload${qs(q)}`, signal),
  async saveWorkloadSettings(body: WorkloadSettings): Promise<WorkloadSettings> {
    return parse(WorkloadSettings, await send('/settings/workload', { method: 'PUT', body }), 'PUT /settings/workload');
  },
  // the Claude connector
  users: (signal?: AbortSignal) => get(z.array(z.object({ id: z.string(), name: z.string(), role: z.string() })), '/users', signal),
  apiTokens: (signal?: AbortSignal) => get(z.array(ApiToken), '/api-tokens', signal),
  async createApiToken(body: ApiTokenCreate): Promise<ApiTokenIssued> {
    return parse(ApiTokenIssued, await send('/api-tokens', { method: 'POST', body }), 'POST /api-tokens');
  },
  async revokeApiToken(id: string): Promise<void> {
    await send(`/api-tokens/${seg(id)}`, { method: 'DELETE' });
  },
  // invoicing
  invoice: (id: string, signal?: AbortSignal) => get(Invoice, `/invoices/${seg(id)}`, signal),
  invoices: (q: { customer?: string; from?: string; to?: string; status?: string }, signal?: AbortSignal) => get(z.array(InvoiceSummary), `/invoices${qs(q)}`, signal),
  async invoiceOrder(orderId: string, body: InvoiceFromOrder = {}): Promise<Invoice> {
    return parse(Invoice, await send(`/orders/${seg(orderId)}/invoice`, { method: 'POST', body }), 'POST invoice');
  },
  async recordLostSale(body: LostSaleCreate, idempotencyKey: string): Promise<z.infer<typeof LostSale>> {
    return parse(LostSale, await send('/lost-sales', { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST /lost-sales');
  },
  lostSales: (q: { from: string; to: string }, signal?: AbortSignal) => get(LostSalesReport, `/reports/lost-sales${qs(q)}`, signal),
  /** change a saved invoice (decision 170): the lines it should have now */
  async editInvoice(id: string, body: InvoiceEdit, idempotencyKey: string): Promise<Invoice> {
    return parse(Invoice, await send(`/invoices/${seg(id)}/edit`, { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST /invoices/edit');
  },
  async invoiceShorts(id: string, body: InvoiceShorts): Promise<Invoice> {
    return parse(Invoice, await send(`/invoices/${seg(id)}/shorts`, { method: 'POST', body }), 'POST /invoices/shorts');
  },
  invoiceAdjustments: (id: string, signal?: AbortSignal) => get(z.array(InvoiceAdjustment), `/invoices/${seg(id)}/adjustments`, signal),
  async voidInvoice(id: string, reason: string, keepCredits?: boolean, order?: 'keep' | 'cancel', dropCreditLines?: boolean): Promise<Invoice> {
    return parse(Invoice, await send(`/invoices/${seg(id)}/void`, { method: 'POST', body: { reason, ...(keepCredits ? { keepCredits } : {}), ...(order ? { order } : {}), ...(dropCreditLines ? { dropCreditLines } : {}) } }), 'POST void');
  },
  /** Delete an invoice (decision 193): voided first when issued; gone from every list, deleted in QuickBooks. */
  async deleteInvoice(id: string, reason: string, order?: 'keep' | 'cancel', dropCreditLines?: boolean): Promise<void> {
    await send(`/invoices/${seg(id)}/delete`, { method: 'POST', body: { reason, ...(order ? { order } : {}), ...(dropCreditLines ? { dropCreditLines } : {}) } });
  },
  async recordPayment(body: PaymentCreate, idempotencyKey: string): Promise<Payment> {
    return parse(Payment, await send('/payments', { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST /payments');
  },
  async receivePayment(body: PaymentReceive, idempotencyKey: string): Promise<PaymentReceipt> {
    return parse(PaymentReceipt, await send('/payments/receive', { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST /payments/receive');
  },
  async applyOnAccount(customerId: string, body: ApplyOnAccount, idempotencyKey: string): Promise<PaymentReceipt> {
    return parse(PaymentReceipt, await send(`/customers/${seg(customerId)}/apply-on-account`, { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST apply-on-account');
  },
  // month-end (decision 106)
  booksSettings: (signal?: AbortSignal) => get(BooksSettings, '/books/settings', signal),
  async setBooksClosed(closedThrough: string | null): Promise<BooksSettings> {
    return parse(BooksSettings, await send('/books/settings', { method: 'PATCH', body: { closedThrough } }), 'PATCH /books/settings');
  },
  inventoryValue: (asOf: string, signal?: AbortSignal) => get(InventoryValue, `/reports/inventory-value${qs({ asOf })}`, signal),
  statementsPack: (signal?: AbortSignal) => get(z.array(Statement), '/reports/statements', signal),
  // end of day (decision 105)
  moneyDay: (date: string, signal?: AbortSignal) => get(DaySummary, `/money/day${qs({ date })}`, signal),
  undeposited: (signal?: AbortSignal) => get(z.array(UndepositedPayment), '/money/undeposited', signal),
  deposits: (signal?: AbortSignal) => get(z.array(DepositSummary), '/money/deposits', signal),
  deposit: (id: string, signal?: AbortSignal) => get(Deposit, `/money/deposits/${seg(id)}`, signal),
  async makeDeposit(body: DepositCreate, idempotencyKey: string): Promise<Deposit> {
    return parse(Deposit, await send('/money/deposits', { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST /money/deposits');
  },
  async closeDay(body: DayClose): Promise<DaySummary> {
    return parse(DaySummary, await send('/money/day/close', { method: 'POST', body }), 'POST /money/day/close');
  },
  // vendor claims (decision 107)
  vendorClaims: (vendorId: string | undefined, signal?: AbortSignal) => get(z.array(VendorClaim), `/vendor-claims${qs({ vendor: vendorId })}`, signal),
  async settleVendorClaim(body: VendorClaimSettle): Promise<{ key: string; outcome: string; amount: number | null }> {
    return parse(z.object({ key: z.string(), outcome: z.string(), amount: z.number().nullable() }), await send('/vendor-claims/settle', { method: 'POST', body }), 'POST settle');
  },
  billMatching: (signal?: AbortSignal) => get(BillMatching, '/vendor-claims/matching', signal),
  async linkReceiptBill(receiptId: string, documentId: string | null): Promise<{ receiptId: string; documentId: string | null }> {
    return parse(z.object({ receiptId: z.string(), documentId: z.string().nullable() }), await send(`/receipts/${seg(receiptId)}/bill`, { method: 'PUT', body: { documentId } }), 'PUT receipt bill');
  },
  async reversePayment(id: string, note: string): Promise<Payment> {
    return parse(Payment, await send(`/payments/${seg(id)}/reverse`, { method: 'POST', body: { note } }), 'POST reverse');
  },
  async reverseCredit(id: string, note: string): Promise<Credit> {
    return parse(Credit, await send(`/credits/${seg(id)}/reverse`, { method: 'POST', body: { note } }), 'POST credit reverse');
  },
  async giveCredit(body: CreditCreate, idempotencyKey: string): Promise<Credit> {
    return parse(Credit, await send('/credits', { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST /credits');
  },
  statement: (customerId: string, signal?: AbortSignal, practice = false) => get(Statement, `/customers/${seg(customerId)}/statement${practice ? '?practice=1' : ''}`, signal),
  ledgerSettings: (signal?: AbortSignal) => get(LedgerSettings, '/ledger/settings', signal),
  async startLedger(balanceFrom: string | null): Promise<LedgerSettings> {
    return parse(LedgerSettings, await send('/ledger/settings', { method: 'PATCH', body: { balanceFrom } }), 'PATCH ledger');
  },
  countSheet: (site?: string | null, signal?: AbortSignal) => get(CountSheet, `/stock/count-sheet${qs(site ? { site } : {})}`, signal),
  // sites and companies
  sites: (signal?: AbortSignal) => get(z.array(Site), '/sites', signal),
  companies: (signal?: AbortSignal) => get(z.array(Company), '/companies', signal),
  async createSite(body: SiteCreate): Promise<Site> {
    return parse(Site, await send('/sites', { method: 'POST', body }), 'POST /sites');
  },
  async patchSite(id: string, body: SitePatch): Promise<Site> {
    return parse(Site, await send(`/sites/${seg(id)}`, { method: 'PATCH', body }), 'PATCH /sites');
  },
  async createCompany(body: CompanyCreate): Promise<Company> {
    return parse(Company, await send('/companies', { method: 'POST', body }), 'POST /companies');
  },
  transfers: (q: { site?: string; status?: string }, signal?: AbortSignal) => get(z.array(Transfer), `/transfers${qs(q)}`, signal),
  async createTransfer(body: TransferCreate, idempotencyKey: string): Promise<Transfer> {
    return parse(Transfer, await send('/transfers', { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }), 'POST /transfers');
  },
  async sendTransfer(id: string): Promise<Transfer> {
    return parse(Transfer, await send(`/transfers/${seg(id)}/send`, { method: 'POST' }), 'POST /transfers/send');
  },
  async receiveTransfer(id: string, body: TransferReceive): Promise<Transfer> {
    return parse(Transfer, await send(`/transfers/${seg(id)}/receive`, { method: 'POST', body }), 'POST /transfers/receive');
  },
  async cancelTransfer(id: string): Promise<Transfer> {
    return parse(Transfer, await send(`/transfers/${seg(id)}/cancel`, { method: 'POST' }), 'POST /transfers/cancel');
  },
  async saveCount(body: CountCreate, idempotencyKey: string): Promise<CountResult> {
    const data = await send('/stock/counts', { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } });
    return parse(CountResult, data, 'POST /stock/counts');
  },
};

/** RFC 4122 v4. crypto.randomUUID only exists on https/localhost, so fall back to getRandomValues. */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (typeof c.randomUUID === 'function') return c.randomUUID();
  const b = c.getRandomValues(new Uint8Array(16));
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40;
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
